import type { GameSnapshot } from "../game/Game";

function formatTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

export class Hud {
  private integrityBar = document.getElementById("bar-integrity");
  private integrityVal = document.getElementById("val-integrity");
  private proclaimBar = document.getElementById("bar-proclaim");
  private proclaimVal = document.getElementById("val-proclaim");
  private runTimer = document.getElementById("run-timer");
  private logEl = document.getElementById("combat-log");
  private overlay = document.getElementById("proclaim-overlay");
  private overlayRef = document.getElementById("proclaim-ref");
  private overlayKaraoke = document.getElementById("proclaim-karaoke");

  render(snap: GameSnapshot): void {
    const r = snap.resources;
    const frac = Math.max(0, Math.min(1, r.integrity / Math.max(1, r.maxIntegrity)));
    if (this.integrityBar) {
      this.integrityBar.style.width = `${frac * 100}%`;
    }
    if (this.integrityVal) {
      this.integrityVal.textContent = String(Math.round(r.integrity));
    }
    // Soft stress on the sanctuary when integrity is low
    const strip = this.integrityBar?.closest(".secret-place-strip");
    if (strip) {
      strip.classList.toggle("integrity-low", frac < 0.35);
      strip.classList.toggle("integrity-critical", frac < 0.2);
    }

    const lock = snap.proclaimLock;
    const lockMax = snap.proclaimLockMax;
    if (this.proclaimBar) {
      if (lockMax > 0 && lock > 0) {
        this.proclaimBar.style.width = `${(lock / lockMax) * 100}%`;
      } else {
        this.proclaimBar.style.width = "0%";
      }
    }
    if (this.proclaimVal) {
      this.proclaimVal.textContent =
        lock > 0.05 ? `${lock.toFixed(1)}s` : "Ready";
    }

    if (this.runTimer) {
      if (snap.endless) {
        this.runTimer.textContent = formatTime(snap.elapsed);
        this.runTimer.title = `Survived · score ${snap.elapsed.toFixed(1)}s`;
        this.runTimer.dataset.mode = "survived";
      } else {
        const left = Math.max(0, snap.duration - snap.elapsed);
        this.runTimer.textContent = formatTime(left);
        this.runTimer.title = "Time left";
        this.runTimer.dataset.mode = "remaining";
        this.runTimer.classList.toggle("is-urgent", left <= 30);
      }
    }

    if (this.logEl) this.logEl.textContent = snap.lastMessage;

    this.renderSettledHunt(snap);
    this.renderKaraoke(snap);
  }

  private huntEl = document.getElementById("settled-hunt");

  private renderSettledHunt(snap: GameSnapshot): void {
    const el = this.huntEl;
    if (!el) return;
    const h = snap.settledHunt;
    if (!h) {
      el.classList.add("hidden");
      el.textContent = "";
      return;
    }
    const layer =
      h.layer.charAt(0).toUpperCase() + h.layer.slice(1);
    const demand = h.named
      ? `wants ${h.demand.charAt(0).toUpperCase() + h.demand.slice(1)}`
      : "unnamed";
    el.classList.remove("hidden");
    el.classList.toggle("is-will", h.onWill);
    el.textContent = h.onWill
      ? `${h.name} · sat on Will · ${demand}`
      : `${h.name} · ${layer} · ${h.remaining} left · ${demand}`;
    el.title = h.claim;
  }

  private renderKaraoke(snap: GameSnapshot): void {
    if (!this.overlay || !this.overlayRef || !this.overlayKaraoke) return;
    // Always visible — toggle speaking vs idle (no layout jump)
    this.overlay.classList.remove("hidden");
    if (snap.proclaimLock <= 0 || !snap.proclaimText) {
      this.overlay.classList.add("is-idle");
      this.overlay.classList.remove("is-speaking");
      this.overlayRef.textContent = "";
      this.overlayKaraoke.textContent = "Ready to proclaim";
      return;
    }
    this.overlay.classList.remove("is-idle");
    this.overlay.classList.add("is-speaking");
    this.overlayRef.textContent = snap.proclaimRef;
    const words = snap.proclaimText.split(/\s+/).filter(Boolean);
    const n = Math.max(0, Math.min(words.length, snap.proclaimWordIndex));
    this.overlayKaraoke.innerHTML = words
      .map((w, i) => {
        const cls = i < n ? "spoken" : i === n ? "current" : "pending";
        return `<span class="kw ${cls}">${escapeHtml(w)}</span>`;
      })
      .join(" ");
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
